import SignOut from 'pages/authentication/sign-out/SignOut';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Region, regionNames } from '../../types/orderTypes';
import Dropdown from '../dropdown/Dropdown';
import AdminNavbarLinks from './AdminNavbarLinks';
import PorterbuddyLogo from './assets/porterbuddy_grey_landscape.svg';
import PorterbuddyLogoMobile from './assets/porterbuddy_logo_logo-only-white.svg';
import Clock from './Clock';
import './Navbar.scss';
import { NavigationDrawer } from './NavigationDrawer';
import { Hamburger } from './Hamburger';

interface NavBarProps {
  availableRegions: Region[];
  selectedRegion: Region;
  userText: string;
  changeRegionCallback: (region: Region) => void;
  limitedAccess: boolean;
}

export function AdminNavBar(props: NavBarProps) {
  const showRegionSelection = props.availableRegions.length > 1;

  return (
    <div className="navbar">
      <div className="navbar__container">
        <div className="desktop-only">
          <span className="navbar__logo">
            <Link to={'/dashboard'} style={{ marginRight: '1em' }}>
              <img
                className="navbar__logo-image"
                src={PorterbuddyLogoMobile}
                alt="Porterbuddy logo"
              />
              <img
                className="navbar__logo-image-narrow"
                src={PorterbuddyLogoMobile}
                alt="Porterbuddy logo"
              />
              <div
                className="desktop-only"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'baseline',
                  color: 'rgb(255, 255, 255)',
                }}
              >
                <div>Dispatch</div>
                <div
                  style={{
                    fontSize: '0.5em',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '12em',
                  }}
                >
                  {props.userText}
                </div>
              </div>
            </Link>
            <div className="navbar__region">
              {showRegionSelection && (
                <div className="navbar__dropdown">
                  <Dropdown
                    names={Object.values(props.availableRegions).map(
                      regionId => regionNames[regionId]
                    )}
                    values={Object.values(props.availableRegions)}
                    placeholder={regionNames[props.selectedRegion]}
                    onChange={region => {
                      props.changeRegionCallback(region);
                    }}
                  />
                </div>
              )}
              {!showRegionSelection && (
                <div style={{ color: '#fff' }}>{props.selectedRegion}</div>
              )}
            </div>

            <div className="navbar__clock desktop-only">
              <Clock />
            </div>
          </span>
        </div>

        <div className="mobile-only navbar__container">
          <span className="navbar__logo">
            <Link to="/">
              <img src={PorterbuddyLogoMobile} alt="Porterbuddy logo" />
            </Link>
            <div>
              <div>Dispatch</div>
              <div
                style={{
                  fontSize: '0.5em',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '12em',
                }}
              >
                {props.userText}
              </div>
            </div>
          </span>
          <div className="navbar__region">
            {showRegionSelection && (
              <div className="navbar__dropdown">
                <Dropdown
                  names={Object.values(props.availableRegions)}
                  placeholder={props.selectedRegion}
                  onChange={region => {
                    props.changeRegionCallback(region);
                  }}
                  customStyle={{ width: '90px' }}
                />
              </div>
            )}
            {!showRegionSelection && (
              <div style={{ color: '#fff' }}>{props.selectedRegion}</div>
            )}
          </div>
        </div>
        <div className="desktop-only">
          <div className="navbar__nav-links">
            <AdminNavbarLinks limitedAccess={props.limitedAccess} />
            <SignOut />
          </div>
        </div>

        <AdminMobileSideMenuBar limitedAccess={props.limitedAccess} />
      </div>
    </div>
  );
}

const adminAccessLinks = [
  { to: '/dashboard', text: 'Dashboard' },
  { to: '/orders', text: 'Orders' },
  { to: '/planner', text: 'Planner' },
  { to: '/shift-planner', text: 'Shift' },
  { to: '/hub', text: 'Hub' },
  { to: '/hub/pre-sort', text: 'Pre sort scanner' },
  { to: '/hub/geo-pre-sort', text: 'Geo Pre sort scanner' },
  { to: '/hub/reg-pre-sort', text: 'Region sort scanner' },
  { to: '/hub/info-scanner', text: 'Info scanner' },
  { to: '/warehouse', text: 'Warehouse' },
  { to: '/monitor', text: 'Monitor' },
  { to: '/users', text: 'Users' },
];

const limitedAccessLinks = [
  { to: '/orders', text: 'Orders' },
  { to: '/hub', text: 'Hub' },
  { to: '/hub/info-scanner', text: 'Info scanner' },
  { to: '/warehouse', text: 'Warehouse' },
  { to: '/monitor', text: 'Monitor' },
];

function AdminMobileSideMenuBar({
  limitedAccess,
}: {
  limitedAccess: boolean;
}): JSX.Element {
  const [sideNavIsOpen, setSideNavIsOpen] = useState(false);
  const [openSideNav, closeSideNav] = [
    () => setSideNavIsOpen(true),
    () => setSideNavIsOpen(false),
  ];

  const navLinks = limitedAccess ? limitedAccessLinks : adminAccessLinks;

  return (
    <>
      <Hamburger onClick={openSideNav} isActive={sideNavIsOpen} />

      <NavigationDrawer
        close={closeSideNav}
        open={openSideNav}
        isOpen={sideNavIsOpen}
        navLinks={navLinks}
      />
    </>
  );
}

export function NotLoggedInNavBar(): JSX.Element {
  return (
    <div className="navbar">
      <div className="navbar__container">
        <div className="desktop-only">
          <span className="navbar__logo">
            <Link to="/sign-in">
              <img
                className="navbar__logo-image"
                src={PorterbuddyLogo}
                alt="Porterbuddy logo"
              />
              <img
                className="navbar__logo-image-narrow"
                src={PorterbuddyLogoMobile}
                alt="Porterbuddy logo"
              />
            </Link>
            <div style={{ width: 10 }} />
          </span>
        </div>
        <div className="mobile-only">
          <span className="navbar__logo">
            <Link to="/sign-in">
              <img src={PorterbuddyLogoMobile} alt="Porterbuddy logo" />
            </Link>
            <div style={{ width: 10 }} />
          </span>
        </div>
      </div>
    </div>
  );
}
